import { useState, useEffect, useCallback } from "react";

import moment from "moment";

import DataTable from "customComponents/Tables/DataTable";
import { useAlert } from "customComponents/Alert";

import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { formatDate, formatTimeSpent } from "../../../utils";
import { deviceLocation, deviceState, deviceType } from "../../../enums";

import { getIndividualEmployeeReport } from "helper/index";
import config from "../../../config";

import Header from "../components/Header";

import { Link } from "@mui/material";

function MDReport({ token, initialPeriod, onLinkExpired, onSetPeriod, onSetAttendanceDialog }) {
  const [reportData, setReportData] = useState({});

  // const [attendanceData, setAttendanceData] = useState([]);
  // const [worklogData, setWorklogData] = useState([]);

  // const [employeeData, setEmployeeData] = useState(null);
  // const [dateInfo, setDateInfo] = useState(null);

  const [tabValue, setTabValue] = useState(0);

  const [period, setPeriod] = useState(initialPeriod);

  const alert = useAlert();

  const attendanceColumns = [
    { Header: "No.", accessor: "no", width: "10%", align: "center" },
    { Header: "recordTime", accessor: "recordTime", width: "20%", align: "center" },
    { Header: "state", accessor: "state", align: "center" },
    { Header: "location", accessor: "location", align: "center" },
    { Header: "type", accessor: "type", align: "center" },
  ];

  const worklogColumns = [
    { Header: "No.", accessor: "no", width: "10%", align: "center" },
    { Header: "Project / Issue", accessor: "issue", width: "20%", align: "center" },
    { Header: "Record time", accessor: "recordTime", align: "center" },
    { Header: "Logged", accessor: "timeSpent", align: "center" },
    { Header: "Created At", accessor: "createdAt", align: "center" },
  ];

  const monthlyColumns = [
    { Header: "No.", accessor: "no", width: "10%", align: "center" },
    { Header: "Date", accessor: "date", width: "20%", align: "center" },
    { Header: "Office Time", accessor: "officeTime", align: "center" },
    { Header: "Break Time", accessor: "breakTime", align: "center" },
    { Header: "Jira Time", accessor: "jiraTime", align: "center" },
    { Header: "Office In", accessor: "officeIn", align: "center" },
    { Header: "Office Out", accessor: "officeOut", align: "center" },
    { Header: "Details", accessor: "action", align: "center" },
  ];

  const handleSetTabValue = (event, newValue) => setTabValue(newValue);
  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    onSetPeriod(newPeriod);
  };

  useEffect(() => {
    fetchData();
  }, [token, period]);

  const openAttendanceDialog = ({ currentTarget }) => {
    const dataJson = currentTarget.getAttribute("data");
    const data = JSON.parse(dataJson);

    onSetAttendanceDialog(data);
  };

  const fetchData = useCallback(async () => {
    if (reportData[period]) {
      return;
    }

    try {
      const res = await getIndividualEmployeeReport(token, period);

      const { range, attendances: rAttendances, worklogs: rWorklogs, ...rest } = res.data;

      let periodData = {};

      if (period === "CURRENT_DAY" || period === "TODAY") {
        const worklogDates = rWorklogs ? Object.keys(rWorklogs) : [];
        const attendanceDates = rAttendances ? Object.keys(rAttendances) : [];

        const dateTimeKeys = Array.from(new Set([...attendanceDates, ...worklogDates]));

        const dateTimeKeyValues = {};

        for (const dateTimeKey of dateTimeKeys) {
          const dateTime = moment(dateTimeKey);
          dateTimeKeyValues[dateTime.format("YYYY-MM-DD")] = dateTimeKey;
        }

        const date = range.to;
        const curDate = dateTimeKeyValues[date];

        const attendances = rAttendances[curDate] ?? [];
        const worklogs = rWorklogs[curDate] ?? [];

        const formattedAttendanceData = formatAttendanceData(attendances);
        const formattedWorklogData = formatWorklogData(worklogs);

        const { officeTime, breakTime } = calculateOfficeTime(attendances);
        const worklogHours = worklogs.reduce((total, log) => total + log.timeSpentSeconds, 0);

        periodData = {
          employeeData: rest,
          dateInfo: {
            date,
            officeTime,
            breakTime,
            worklogHours,
            shortHours:
              officeTime < config.MINIMUM_OFFICE_TIME ? config.MINIMUM_OFFICE_TIME - officeTime : 0,
            shortWorklogHours:
              worklogHours < config.MINIMUM_JIRA_TIME ? config.MINIMUM_JIRA_TIME - worklogHours : 0,
          },
          attendanceData: formattedAttendanceData,
          worklogData: formattedWorklogData,
        };
      } else {
        const { rows, totalTime, totalBreakTime, totalJiraTime, shortTime, shortJiraTime } =
          formatData(
            { attendances: rAttendances, worklogs: rWorklogs, employeeData: rest },
            { ...range }
          );

        periodData = {
          employeeData: rest,
          dateInfo: {
            date: range.to,
            officeTime: totalTime,
            breakTime: totalBreakTime,
            worklogHours: totalJiraTime,
            shortHours: shortTime,
            shortWorklogHours: shortJiraTime,
          },
          rows,
        };
      }

      setReportData({
        ...reportData,
        [period]: periodData,
      });
    } catch (error) {
      if (error.response?.status === 401) {
        console.log("link expired");
        onLinkExpired(true);
      } else {
        const title = error.response ? error.response.statusText : "Unknown error";
        const message =
          error.response && error.response.data ? error.response.data.message : error.message;

        alert.show(title, message);
      }
    }
  }, [token, period]);

  const formatData = useCallback((res, filter) => {
    const { to, from } = filter;

    const startDate = moment(`${from} 00:00`, "YYYY-MM-DD HH:mm");
    const endDate = moment(`${to} 23:59`, "YYYY-MM-DD HH:mm");

    let iDate = moment(startDate);

    let dateArr = [];

    do {
      const formattedDate = iDate.format("YYYY-MM-DD");

      dateArr.push({
        date: formattedDate,
        day: iDate.day(),
        formattedDate: iDate.format("ddd, MMM D"),
      });

      iDate.add(1, "days");
    } while (iDate.isBefore(endDate));

    const { attendances, worklogs, employeeData } = res;

    const worklogDates = worklogs ? Object.keys(worklogs) : [];
    const attendanceDates = attendances ? Object.keys(attendances) : [];

    const dateTimeKeys = Array.from(new Set([...attendanceDates, ...worklogDates]));

    const dateTimeKeyValues = {};

    for (const dateTimeKey of dateTimeKeys) {
      const dateTime = moment(dateTimeKey);
      dateTimeKeyValues[dateTime.format("YYYY-MM-DD")] = dateTimeKey;
    }

    let rows = [];

    let totalTime = 0;
    let totalBreakTime = 0;
    let totalJiraTime = 0;

    let officeDays = 0;
    let jiraDays = 0;

    for (const index in dateArr) {
      const { date, day: dayOfWeek, formattedDate } = dateArr[index];
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      const num = parseInt(index) + 1;

      const isAbsent =
        !dateTimeKeyValues[date] && !isWeekend && moment(date).isBefore(moment(), "day");

      if (dateTimeKeyValues[date]) {
        const curAttendances = attendances[dateTimeKeyValues[date]];
        const curWorklogs = worklogs[dateTimeKeyValues[date]];

        let officeTime = 0;
        let breakTime = 0;
        let jiraTime = 0;

        let startTime = "";
        let lastTime = "";

        if (curAttendances && curAttendances.length > 1) {
          const firstAttendance = curAttendances[0];
          const latestAttendance = curAttendances[curAttendances.length - 1];

          const firstRecordTime = moment(firstAttendance.recordTime, "yyyy-MM-DDTHH:mm:ss.SSSZ");
          const latestRecordTime = moment(latestAttendance.recordTime, "yyyy-MM-DDTHH:mm:ss.SSSZ");

          startTime = firstRecordTime.format("hh:mma");
          lastTime = latestRecordTime.format("hh:mma");

          const totalOfficeTime = latestRecordTime.diff(firstRecordTime, "seconds");

          //Admins and office Boys have more check ins and check outs so only calculating their first and last thumb
          if (curAttendances.length < 30) {
            for (var i = 1; i < curAttendances.length; i++) {
              const lastAttendance = curAttendances[i - 1];
              const curAttendance = curAttendances[i];

              const lastRecordTime = moment(lastAttendance.recordTime, "yyyy-MM-DDTHH:mm:ss.SSSZ");
              const curRecordTime = moment(curAttendance.recordTime, "yyyy-MM-DDTHH:mm:ss.SSSZ");

              const diff = curRecordTime.diff(lastRecordTime, "seconds");

              // if diff is greater than or equal to MINIMUM_BREAK_TIME
              if (diff >= config.MINIMUM_BREAK_TIME) {
                if (
                  lastAttendance.device.state === "checkOut" &&
                  curAttendance.device.state === "checkIn"
                ) {
                  breakTime += diff;
                } else if (
                  (lastAttendance.device.state === "checkOut" &&
                    curAttendance.device.state === "checkOut") ||
                  (lastAttendance.device.state === "checkIn" &&
                    curAttendance.device.state === "checkIn")
                ) {
                  breakTime += config.MINIMUM_BREAK_TIME; // assuming here, user has not checked in so he might have spent config.MINIMUM_BREAK_TIME outside
                }
              }
            }
          }

          officeTime = totalOfficeTime - breakTime;
          totalTime += officeTime;
          totalBreakTime += breakTime;

          officeDays++;
        }

        if (curWorklogs && curWorklogs.length > 0) {
          for (const worklog of curWorklogs) {
            const { timeSpentSeconds } = worklog;

            jiraTime += timeSpentSeconds;
            totalJiraTime += timeSpentSeconds;
          }

          jiraDays++;
        }

        rows.push({
          no: (
            <MDTypography variant="caption" fontWeight="medium">
              {num}
            </MDTypography>
          ),
          date: (
            <MDTypography variant="caption" fontWeight="medium">
              {formattedDate}
            </MDTypography>
          ),
          officeTime: (
            <MDTypography variant="caption" fontWeight="medium">
              {officeTime > 0 ? (officeTime / 3600).toFixed(2) + "h" : "_"}
            </MDTypography>
          ),
          breakTime: (
            <MDTypography variant="caption" fontWeight="medium">
              {breakTime > 0 ? (breakTime / 3600).toFixed(2) + "h" : "_"}
            </MDTypography>
          ),
          jiraTime: (
            <MDTypography variant="caption" fontWeight="medium" color="info">
              {jiraTime > 0 ? (jiraTime / 3600).toFixed(2) + "h" : "_"}
            </MDTypography>
          ),
          officeIn: (
            <MDTypography variant="caption" fontWeight="medium">
              {startTime || "_"}
            </MDTypography>
          ),
          officeOut: (
            <MDTypography variant="caption" fontWeight="medium">
              {lastTime || "_"}
            </MDTypography>
          ),
          action: (
            <MDTypography
              id={`${index}-action`}
              data={JSON.stringify({
                name: employeeData.name,
                date,
                attendances: curAttendances,
                worklogs: curWorklogs,
              })}
              sx={{ cursor: "pointer" }}
              onClick={openAttendanceDialog}
            >
              <Icon>open_in_new</Icon>
            </MDTypography>
          ),
        });
      } else if (isAbsent) {
        // Absent
        rows.push({
          no: (
            <MDTypography variant="caption" fontWeight="medium">
              {num}
            </MDTypography>
          ),
          date: (
            <MDTypography variant="caption" fontWeight="medium">
              {formattedDate}
            </MDTypography>
          ),
          officeTime: (
            <MDTypography variant="caption" fontWeight="medium" sx={{ color: "red" }}>
              A
            </MDTypography>
          ),
          breakTime: (
            <MDTypography variant="caption" fontWeight="medium" sx={{ color: "red" }}>
              A
            </MDTypography>
          ),
          jiraTime: (
            <MDTypography variant="caption" fontWeight="medium" sx={{ color: "red" }}>
              A
            </MDTypography>
          ),
          officeIn: (
            <MDTypography variant="caption" fontWeight="medium" sx={{ color: "red" }}>
              A
            </MDTypography>
          ),
          officeOut: (
            <MDTypography variant="caption" fontWeight="medium" sx={{ color: "red" }}>
              A
            </MDTypography>
          ),
          action: (
            <MDTypography variant="caption" fontWeight="medium">
              _
            </MDTypography>
          ),
        });
      } else {
        rows.push({
          no: (
            <MDTypography variant="caption" fontWeight="medium">
              {num}
            </MDTypography>
          ),
          date: (
            <MDTypography variant="caption" fontWeight="medium">
              {formattedDate}
            </MDTypography>
          ),
          officeTime: (
            <MDTypography variant="caption" fontWeight="medium">
              _
            </MDTypography>
          ),
          breakTime: (
            <MDTypography variant="caption" fontWeight="medium">
              _
            </MDTypography>
          ),
          jiraTime: (
            <MDTypography variant="caption" fontWeight="medium" color="info">
              _
            </MDTypography>
          ),
          officeIn: (
            <MDTypography variant="caption" fontWeight="medium">
              _
            </MDTypography>
          ),
          officeOut: (
            <MDTypography variant="caption" fontWeight="medium">
              _
            </MDTypography>
          ),
          action: (
            <MDTypography variant="caption" fontWeight="medium">
              _
            </MDTypography>
          ),
        });
      }
    }

    const totalOfficeHoursRequired = officeDays * config.MINIMUM_OFFICE_TIME;
    const totalJiraHoursRequired = jiraDays * config.MINIMUM_JIRA_TIME;

    const shortTime =
      totalTime < totalOfficeHoursRequired ? totalOfficeHoursRequired - totalTime : 0;
    const shortJiraTime =
      totalJiraTime < totalJiraHoursRequired ? totalJiraHoursRequired - totalJiraTime : 0;

    return { rows, totalTime, totalBreakTime, totalJiraTime, shortTime, shortJiraTime };
  }, []);

  const formatAttendanceData = (attendances) => {
    const rows = attendances.map((attendance, index) => {
      const { recordTime, device } = attendance;

      return {
        no: (
          <MDTypography variant="caption" fontWeight="medium">
            {index + 1}
          </MDTypography>
        ),
        recordTime: (
          <MDTypography variant="caption" fontWeight="medium">
            {formatDate(recordTime)}
          </MDTypography>
        ),
        state: (
          <MDTypography variant="caption" fontWeight="medium">
            {deviceState.value(device?.state)}
          </MDTypography>
        ),
        location: (
          <MDTypography variant="caption" fontWeight="medium">
            {deviceLocation.value(device?.location)}
          </MDTypography>
        ),
        type: (
          <MDTypography variant="caption" fontWeight="medium">
            {deviceType.value(device?.type)}
          </MDTypography>
        ),
      };
    });

    return rows;
  };

  const calculateOfficeTime = (attendances) => {
    let officeTime = 0;
    let breakTime = 0;

    if (attendances.length > 1) {
      const firstAttendance = attendances[0];
      const latestAttendance = attendances[attendances.length - 1];

      const firstRecordTime = moment(firstAttendance.recordTime, "yyyy-MM-DDTHH:mm:ss.SSSZ");
      const latestRecordTime =
        period === "TODAY"
          ? moment()
          : moment(latestAttendance.recordTime, "yyyy-MM-DDTHH:mm:ss.SSSZ");

      const totalOfficeTime = latestRecordTime.diff(firstRecordTime, "seconds");

      //Admins and office Boys have more check ins and check outs so only calculating their first and last thumb
      if (attendances.length < 30) {
        for (var i = 1; i < attendances.length; i++) {
          const lastAttendance = attendances[i - 1];
          const curAttendance = attendances[i];

          const lastRecordTime = moment(lastAttendance.recordTime, "yyyy-MM-DDTHH:mm:ss.SSSZ");
          const curRecordTime = moment(curAttendance.recordTime, "yyyy-MM-DDTHH:mm:ss.SSSZ");

          const diff = curRecordTime.diff(lastRecordTime, "seconds");

          // if diff is greater than or equal to minimum break time
          if (diff >= config.MINIMUM_BREAK_TIME) {
            if (
              lastAttendance.device.state === "checkOut" &&
              curAttendance.device.state === "checkIn"
            ) {
              breakTime += diff;
            } else if (
              (lastAttendance.device.state === "checkOut" &&
                curAttendance.device.state === "checkOut") ||
              (lastAttendance.device.state === "checkIn" &&
                curAttendance.device.state === "checkIn")
            ) {
              breakTime += config.MINIMUM_BREAK_TIME; // assuming here, user has not checked in so he might have spent MINIMUM_BREAK_TIME outside
            }
          }
        }
      }

      officeTime = totalOfficeTime - breakTime;
    }

    return {
      officeTime,
      breakTime,
    };
  };

  const formatWorklogData = (res) => {
    return res.map((value, index) => {
      const {
        worklogStarted,
        employee,
        issueSummary,
        issueIdOrKey,
        projectName,
        // projectKey,
        timeSpentSeconds,
        worklogCreated,
      } = value;

      return {
        no: (
          <MDTypography variant="caption" fontWeight="medium">
            {index + 1}
          </MDTypography>
        ),
        issue: (
          <Link href={`${config.JIRA_API_URL}/browse/${issueIdOrKey}`} target="_blank">
            <MDBox
              display="flex"
              alignItems="left"
              flexDirection="column"
              lineHeight={1}
              sx={{ maxWidth: "240px" }}
            >
              {/* <MDBox lineHeight={1} ml={0}> */}
              <MDTypography
                variant="caption"
                fontWeight="medium"
                // color="text"
                noWrap={false}
                sx={{ wordBreak: "break-word" }}
              >
                {`${issueIdOrKey}:${issueSummary}`}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                {projectName}
              </MDTypography>
              {/* </MDBox> */}
            </MDBox>
          </Link>
        ),
        employeeName: (
          <MDTypography variant="caption" fontWeight="medium">
            {employee?.name}
          </MDTypography>
        ),
        recordTime: (
          <MDTypography variant="caption" fontWeight="medium">
            {formatDate(worklogStarted)}
          </MDTypography>
        ),
        timeSpent: (
          <MDTypography variant="caption" fontWeight="medium">
            {formatTimeSpent(timeSpentSeconds)}
          </MDTypography>
        ),
        createdAt: (
          <MDTypography variant="caption" fontWeight="medium">
            {formatDate(worklogCreated)}
          </MDTypography>
        ),
      };
    });
  };

  const {
    employeeData,
    dateInfo,
    attendanceData,
    worklogData,
    rows: monthlyRows,
  } = reportData[period] ?? {};

  const table =
    period === "CURRENT_DAY" || period === "TODAY"
      ? tabValue === 0
        ? { rows: attendanceData, columns: attendanceColumns }
        : { rows: worklogData, columns: worklogColumns }
      : { columns: monthlyColumns, rows: monthlyRows };

  const hasTabs = worklogData && worklogData.length > 0;
  const defaultPageSize = period === "CURRENT_DAY" || period === "TODAY" ? 10 : 31;

  return (
    (employeeData && dateInfo && (
      <Header
        employeeData={employeeData}
        dateInfo={dateInfo}
        tabValue={tabValue}
        handlePeriodChange={handlePeriodChange}
        period={period}
        handleSetTabValue={handleSetTabValue}
        hasTabs={hasTabs}
      >
        <DataTable
          table={table}
          showTotalEntries={true}
          isSorted={false}
          noEndBorder
          defaultPageSize={defaultPageSize}
          entriesPerPage={false}
        />
      </Header>
    )) ??
    null
  );
}

export default MDReport;
